/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Breadcrumbs from 'components/Breadcrumbs';
import FullHeight from 'components/FullHeight';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import TeamForm from 'pages/EditTeam/EditTeamForm';
import { TeamData } from 'pages/EditTeam/types';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
  updateMultiOrgTeam,
  useAddTeamLearner,
  useGetTeam,
  updateSingleOrgTeam,
  TeamEditRequest,
  MultiOrgTeamEditRequest,
} from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useRequestErrorContext } from 'components/RequestErrorDialog/RequestErrorProvider';
import { useQueryClient } from '@tanstack/react-query';
import getOrganisationsDiff from 'utils/getOrganisationDiff';
import { MULTI_ORG, QUERY_STATUS, SINGLE_ORG } from 'variables';
import hasTeamDetailsChanged from './hasTeamDetailsChanged';

const EditTeam = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const queryClient = useQueryClient();
  const { mutateAsync: addLearner } = useAddTeamLearner(queryClient);
  const { status: teamStatus, data: team } = useGetTeam(teamId || '');
  const isSingleOrgTeam = team?.organisationContext?.type === SINGLE_ORG;

  const organisationId = team?.organisationContext?.organisations[0].id ?? '';

  const updateTeam = isSingleOrgTeam
    ? (payload: TeamEditRequest) =>
        updateSingleOrgTeam({
          ...payload,
          teamId: teamId ?? '',
          organisationId,
        })
    : updateMultiOrgTeam;

  const { openErrorModal } = useRequestErrorContext();

  const onSubmit = async ({
    learners,
    selectedOrgs = [],
    ...rawFormData
  }: TeamData) => {
    const newIds = learners.map(({ id }) => id);
    const teamDetailsChanged = hasTeamDetailsChanged(team!, {
      ...rawFormData,
      selectedOrgs,
    });

    if (!teamDetailsChanged && newIds.length === 0) {
      return;
    }

    const requestBody: MultiOrgTeamEditRequest = {
      teamId: teamId || '',
      formData: {
        ...rawFormData,
        addOrganisations: [],
        removeOrganisations: [],
      },
    };

    const { add, remove } = getOrganisationsDiff(
      team?.organisationContext?.organisations ?? [],
      selectedOrgs,
    );

    requestBody.formData.addOrganisations = add;
    requestBody.formData.removeOrganisations = remove;

    try {
      const updatedTeamId = teamDetailsChanged
        ? (await updateTeam(requestBody)).id
        : (teamId ?? '');

      await Promise.all([
        ...newIds.map((newId) =>
          addLearner({ learnerId: newId, teamId: updatedTeamId }),
        ),
      ]);
      navigate(`/teams/${updatedTeamId}`);
    } catch {
      openErrorModal();
    }
  };
  if (teamStatus === QUERY_STATUS.PENDING) {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (teamStatus === QUERY_STATUS.ERROR) {
    return (
      <FullScreenError message={t('ui.event-management.teams.error-loading')} />
    );
  }
  return (
    <>
      <Helmet>
        <title>
          {t('ui.event-management.title.edit-team', {
            teamName: team!.name,
          })}
        </title>
      </Helmet>
      <FullHeight backgroundColor="white">
        <Container maxWidth="lg">
          <Box
            sx={{
              py: (theme) => theme.spacing(spacingSizeMap.M),
            }}
          >
            <Breadcrumbs
              crumbs={{
                '/': t('ui.event-management.events.nav.home'),
                '/learners': t('ui.event-management.learners-and-teams.title'),
                [`/teams/${teamId}`]: team!.name,
              }}
              activeText={t('ui.event-management.teams.edit')}
            />
          </Box>
          <Grid container>
            <Grid size={{ xs: 12, md: 8 }}>
              <Grid
                container
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Grid>
                  <TextV2 component="h1" variant="h2">
                    {t('ui.event-management.teams.edit')}
                  </TextV2>
                </Grid>
              </Grid>
              <Box
                sx={{
                  my: (theme) => theme.spacing(spacingSizeMap.L),
                }}
              />
              <Divider />
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.L),
                }}
              />
              {team?.organisationContext?.type === MULTI_ORG && (
                <Alert severity="warning">
                  {t('ui.event-management.learners.teams.multi-org-warning')}
                </Alert>
              )}
              <TeamForm team={team!} onSubmit={onSubmit}>
                {({ hasChanges, isSubmitting }) => (
                  <Grid
                    container
                    sx={{
                      justifyContent: 'flex-end',
                      '&&': { mt: 0 },
                    }}
                  >
                    <Button
                      style={{ marginTop: '0' }}
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={!hasChanges || isSubmitting}
                    >
                      {t('ui.event-management.teams.edit.edit-button')}
                    </Button>
                  </Grid>
                )}
              </TeamForm>
            </Grid>
          </Grid>
        </Container>
      </FullHeight>
    </>
  );
};

export default EditTeam;
/* eslint-enable @typescript-eslint/no-non-null-assertion */
